import { useForm } from '@inertiajs/react';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toastFlash } from '@/lib/flash-toast';
import type { SponsorCreditRow, SponsorHackerOption } from '@/types';

interface EditSponsorCreditDialogProps {
  sponsorId: string;
  sponsorSlug: string;
  eventSlug: string;
  credit: SponsorCreditRow;
  hackerOptions: SponsorHackerOption[];
}

export function EditSponsorCreditDialog({
  sponsorId,
  sponsorSlug,
  eventSlug,
  credit,
  hackerOptions,
}: EditSponsorCreditDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    sponsorId,
    hackerProfileId: credit.hackerProfileId,
    url: credit.url ?? '',
    notes: credit.notes ?? '',
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.patch(
      `/admin/${eventSlug}/sponsors/${sponsorSlug}/credits/${credit.id}`,
      {
        preserveScroll: true,
        onSuccess: (page) => {
          toastFlash(page, 'Credit updated successfully!');
          setOpen(false);
        },
      },
    );
  };

  const comboboxOptions = hackerOptions.map((h) => ({
    value: h.hackerProfileId,
    label: `${h.hackerName} (${h.hackerEmail})`,
  }));

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Credit</DialogTitle>
            <DialogDescription>
              Update credit for {credit.hackerName}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-credit-hacker-${credit.id}`}>
                Hacker
              </Label>
              <Combobox
                options={comboboxOptions}
                value={data.hackerProfileId}
                onValueChange={(value) => setData('hackerProfileId', value)}
                placeholder="Select a hacker..."
                searchPlaceholder="Search by name or email..."
                emptyText="No hackers found."
                disabled={processing}
              />
              {errors.hackerProfileId && (
                <p className="text-destructive text-sm">
                  {errors.hackerProfileId}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-credit-url-${credit.id}`}>
                URL (Optional)
              </Label>
              <Input
                id={`edit-sponsor-credit-url-${credit.id}`}
                disabled={processing}
                value={data.url}
                onChange={(e) => setData('url', e.target.value)}
              />
              {errors.url && (
                <p className="text-destructive text-sm">{errors.url}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-sponsor-credit-notes-${credit.id}`}>
                Notes (Optional)
              </Label>
              <Textarea
                id={`edit-sponsor-credit-notes-${credit.id}`}
                disabled={processing}
                rows={4}
                value={data.notes}
                onChange={(e) => setData('notes', e.target.value)}
              />
              {errors.notes && (
                <p className="text-destructive text-sm">{errors.notes}</p>
              )}
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
